import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/AuthContext";
import { LogOut, FileText, MapPin, AlertTriangle, HelpCircle, MessageSquare, Inbox, Home, Lightbulb, Download, CalendarDays, Search } from "lucide-react";
import config from "../../config/stateConfig";

const NAV = [
  { to: "/admin", icon: Inbox, label: "Leads", end: true },
  { to: "/admin/content-strategy", icon: Lightbulb, label: "Content Strategy" },
  { to: "/admin/calendar", icon: CalendarDays, label: "Calendar" },
  { to: "/admin/blog", icon: FileText, label: "Blog Posts" },
  { to: "/admin/cities", icon: MapPin, label: "City Pages" },
  { to: "/admin/accident-types", icon: AlertTriangle, label: "Accident Types" },
  { to: "/admin/faqs", icon: HelpCircle, label: "FAQs" },
  { to: "/admin/testimonials", icon: MessageSquare, label: "Testimonials" },
  { to: "/admin/seo-tools", icon: Search, label: "SEO Tools" },
  { to: "/admin/exports", icon: Download, label: "Exports" },
];

export default function AdminLayout() {
  const { user, logout, checking } = useAuth();
  const nav = useNavigate();
  if (checking) return <div className="min-h-screen grid place-items-center text-charcoal-soft">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen flex bg-bone">
      <aside className="hidden md:flex flex-col w-64 border-r border-black/5 bg-white">
        <div className="p-6 border-b border-black/5">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-forest grid place-items-center text-bone font-display font-black text-lg">O</div>
            <div>
              <div className="font-display font-bold text-forest text-sm">{config.brand}</div>
              <div className="text-[10px] uppercase tracking-wider text-charcoal-soft font-bold">Admin CMS</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${
                  isActive ? "bg-forest text-bone" : "text-charcoal hover:bg-bone-alt"
                }`
              }
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-black/5">
          <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-charcoal-soft hover:bg-bone-alt mb-2" data-testid="admin-view-site">
            <Home className="w-4 h-4" /> View site
          </Link>
          <button
            onClick={async () => { await logout(); nav("/admin/login"); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-charcoal hover:bg-red-50 hover:text-red-600 transition"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-black/5 flex items-center justify-between p-3">
        <div className="font-display font-bold text-forest">Admin</div>
        <button
          onClick={async () => { await logout(); nav("/admin/login"); }}
          className="text-sm text-red-600 font-semibold"
          data-testid="mobile-admin-logout"
        >
          Logout
        </button>
      </div>

      <main className="flex-1 p-4 md:p-10 pt-16 md:pt-10 overflow-x-auto">
        <div className="md:hidden flex gap-2 mb-4 overflow-x-auto pb-2">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `shrink-0 px-3 py-2 rounded-full text-xs font-bold transition ${
                  isActive ? "bg-forest text-bone" : "bg-white border border-black/10"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <Outlet />
      </main>
    </div>
  );
}
